import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import {
  Box, Typography, Grid, Button, TextField, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, Alert,
  Skeleton, CircularProgress, Chip, Paper,
  List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  useMediaQuery, useTheme, IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import UploadIcon from '@mui/icons-material/Upload';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import { useDms } from '../contexts/DmsContext';
import { dmsApi } from '../services/dmsApi';
import DocumentCard from '../components/DocumentCard';
import UploadZone from '../components/UploadZone';
import { useSnackbar } from '../contexts/SnackbarContext';
import BulkActionBar from '../components/BulkActionBar';
import DuplicateWarning from '../components/DuplicateWarning';
import FolderTree from '../components/FolderTree';
import FileTypeIcon from '../components/FileTypeIcon';
import ClassificationBadge from '../components/ClassificationBadge';
import useDmsPalette from '../hooks/useDmsPalette';
import { format } from 'date-fns';

export default function VaultBrowser({ onDocumentSelect, uploadOpen: propUploadOpen, setUploadOpen: propSetUploadOpen }) {
  const MP = useDmsPalette();
  const { vaultId } = useParams();
  const outletContext = useOutletContext() || {};
  const { uploadTrigger } = outletContext;
  const { documents, loading, error, selectedVault, setSelectedVault, selectedFolder, setSelectedFolder, loadDocuments } = useDms();
  const { showSnack } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ classification: '' });
  const [localUploadOpen, setLocalUploadOpen] = useState(false);
  const uploadOpen = propUploadOpen !== undefined ? propUploadOpen : localUploadOpen;
  const setUploadOpen = propSetUploadOpen || setLocalUploadOpen;
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadForm, setUploadForm] = useState({ title: '', description: '', tags: '', status: 'draft', classification: 'unclassified' });
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [duplicates, setDuplicates] = useState([]);

  useEffect(() => {
    if (vaultId && vaultId !== selectedVault) {
      setSelectedVault(vaultId);
      setSelectedFolder(null);
    }
  }, [vaultId, selectedVault, setSelectedVault, setSelectedFolder]);

  useEffect(() => {
    if (uploadTrigger > 0) setUploadOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadTrigger]);

  useEffect(() => {
    const params = {};
    if (filters.classification) params.classification = filters.classification;
    loadDocuments(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadDocuments, selectedVault, selectedFolder, filters.classification]);

  const toggleSelect = useCallback((id, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  }, []);

  const exitSelectionMode = useCallback(() => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  }, []);

  const runBulk = async (action, extra = {}) => {
    const ids = [...selectedIds];
    const res = await dmsApi.bulkOperation({ action, documentIds: ids, ...extra });
    const failed = res.failed?.length || 0;
    const success = res.success?.length || 0;
    if (failed > 0) {
      showSnack(`${success} succeeded, ${failed} failed`, failed === ids.length ? 'error' : 'warning');
    } else {
      showSnack(`${success} document(s) updated`, 'success');
    }
    exitSelectionMode();
    loadDocuments();
  };

  const handleBulkArchive = async () => {
    try {
      await runBulk('archive');
    } catch (err) {
      showSnack(err.error || 'Bulk archive failed', 'error');
    }
  };

  const handleBulkClassify = async (classification) => {
    try {
      await runBulk('classify', { classification });
    } catch (err) {
      showSnack(err.error || 'Bulk classify failed', 'error');
    }
  };

  const handleBulkDelete = async () => {
    try {
      await runBulk('delete');
    } catch (err) {
      showSnack(err.error || 'Bulk delete failed', 'error');
    }
  };


  const handleSearch = (e) => {
    if (e.key === 'Enter') loadDocuments({ search });
  };

  const analyzeWithAI = async (file) => {
    if (!file || file.size > 10 * 1024 * 1024) return; // skip files > 10MB
    setAiAnalyzing(true);
    setAiSuggestions(null);
    try {
      // Use filename-based classification hint (doc id is null pre-upload)
      const res = await dmsApi.aiClassifyPreview({
        filename: file.name,
        mimeType: file.type,
      });
      if (res) {
        setAiSuggestions(res);
        if (res.vault) {
          setUploadForm(prev => ({ ...prev, vault: res.vault }));
        }
        if (res.classification) {
          setUploadForm(prev => ({ ...prev, classification: res.classification }));
        }
        if (res.tags?.length > 0) {
          setUploadForm(prev => ({ ...prev, tags: res.tags.join(', ') }));
        }
      }
    } catch {
      // AI analysis failed silently — user can still upload manually
    } finally {
      setAiAnalyzing(false);
    }
  };

  const handleFileDrop = async (f) => {
    setUploadFile(f);
    setUploadForm((prev) => ({ ...prev, title: f.name }));
    setAiSuggestions(null);
    setDuplicates([]);
    analyzeWithAI(f);
    // Duplicate detection using filename as text query
    const nameQuery = f.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ');
    try {
      const res = await dmsApi.detectDuplicates({ text: nameQuery });
      const list = Array.isArray(res) ? res : (res && Array.isArray(res.data) ? res.data : (res && Array.isArray(res.duplicates) ? res.duplicates : []));
      setDuplicates(list);
    } catch {
      // silent fail — duplicate detection is non-blocking
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('vault', selectedVault);
      formData.append('title', uploadForm.title || uploadFile.name);
      formData.append('description', uploadForm.description);
      formData.append('tags', uploadForm.tags);
      formData.append('status', uploadForm.status);
      formData.append('classification', uploadForm.classification || 'unclassified');
      await dmsApi.uploadDocument(formData);
      setUploadOpen(false);
      setUploadFile(null);
      loadDocuments();
    } catch (err) {
      showSnack(err.error || 'Upload failed', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.title}"?`)) return;
    try {
      await dmsApi.deleteDocument(doc._id);
      loadDocuments();
    } catch (err) {
      showSnack(err.error || 'Delete failed', 'error');
    }
  };

  return (
    <Box>
      {/* Header bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5" fontWeight={700} sx={{ textTransform: 'capitalize', flex: 1, fontSize: { xs: '1.1rem', md: '1.5rem' } }}>
          {selectedVault} Vault
        </Typography>
        {/* Upload button — desktop only; mobile uses the FAB in App.js */}
        {!isMobile && selectedVault && !selectionMode && (
          <Button variant="outlined" startIcon={<CheckBoxOutlineBlankIcon />} onClick={() => setSelectionMode(true)} size="small">
            Select
          </Button>
        )}
        {!isMobile && selectionMode && (
          <Button variant="outlined" color="inherit" onClick={exitSelectionMode} size="small">Cancel</Button>
        )}
        {!isMobile && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setUploadOpen(true)} size="small">
            Upload
          </Button>
        )}
      </Box>

      {/* Search + Filter */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small" placeholder="Search documents..."
          value={search} onChange={e => setSearch(e.target.value)} onKeyDown={handleSearch}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment> }}
          sx={{ flex: 1, minWidth: 180 }}
        />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Filter</InputLabel>
          <Select
            value={filters.classification || ''}
            onChange={e => setFilters(prev => ({ ...prev, classification: e.target.value }))}
            label="Filter"
          >
            <MenuItem value="">All</MenuItem>
            {['unclassified', 'internal', 'confidential', 'restricted', 'secret'].map(c => (
              <MenuItem key={c} value={c} sx={{ textTransform: 'capitalize' }}>{c}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* Mobile selection toggle */}
        {isMobile && selectedVault && !selectionMode && (
          <Button variant="outlined" size="small" onClick={() => setSelectionMode(true)} sx={{ px: 1.5 }}>
            <CheckBoxOutlineBlankIcon fontSize="small" />
          </Button>
        )}
        {isMobile && selectionMode && (
          <Button variant="outlined" color="inherit" size="small" onClick={exitSelectionMode}>✕</Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
        <Paper sx={{ width: { xs: '100%', md: 220 }, flexShrink: 0, p: 1, display: { xs: 'none', md: 'block' } }}>
          <FolderTree
            vault={selectedVault}
            selectedFolderId={selectedFolder}
            onFolderSelect={(folder) => setSelectedFolder(folder?._id || null)}
          />
        </Paper>

        <Box sx={{ flex: 1, minWidth: 0 }}>
      {loading ? (
        // Skeleton adapts: list rows on mobile, cards on desktop
        isMobile ? (
          <Box>
            {[1, 2, 3, 4, 5].map(n => (
              <Skeleton key={n} variant="rectangular" height={64} sx={{ borderRadius: 1, mb: 1 }} />
            ))}
          </Box>
        ) : (
          <Grid container spacing={2}>
            {[1, 2, 3, 4, 5, 6].map(n => (
              <Grid item xs={12} sm={6} md={4} key={n}>
                <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 2 }} />
              </Grid>
            ))}
          </Grid>
        )
      ) : documents.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <FolderOpenIcon sx={{ fontSize: 80, color: 'text.secondary', opacity: 0.2 }} />
          <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>No documents found</Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>Upload your first document to this vault</Typography>
          <Button variant="contained" startIcon={<UploadIcon />} onClick={() => setUploadOpen(true)}>Upload Document</Button>
        </Box>
      ) : isMobile ? (
        /* ── Mobile: compact list rows ── */
        <List disablePadding sx={{ borderRadius: 1, overflow: 'hidden', border: `1px solid ${MP.border}` }}>
          {documents.map((doc, idx) => (
            <ListItem
              key={doc._id}
              disablePadding
              divider={idx < documents.length - 1}
              sx={{ bgcolor: MP.surface }}
            >
              <ListItemButton
                onClick={() => selectionMode
                  ? toggleSelect(doc._id, !selectedIds.has(doc._id))
                  : onDocumentSelect(doc)
                }
                sx={{ py: 1.2, px: 1.5, gap: 1 }}
              >
                {selectionMode && (
                  <input
                    type="checkbox"
                    checked={selectedIds.has(doc._id)}
                    onChange={() => toggleSelect(doc._id, !selectedIds.has(doc._id))}
                    style={{ width: 18, height: 18, marginRight: 4, accentColor: MP.blue }}
                    onClick={e => e.stopPropagation()}
                  />
                )}
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <FileTypeIcon mimeType={doc.mimeType} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {doc.title}
                    </Typography>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mt: 0.2, flexWrap: 'wrap' }}>
                      <Chip label={doc.status} size="small" sx={{ height: 16, fontSize: '0.6rem' }} />
                      <ClassificationBadge classification={doc.classification} size="tiny" />
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem' }}>
                        {format(new Date(doc.updatedAt), 'MMM d')}
                      </Typography>
                    </Box>
                  }
                />
                <ChevronRightIcon sx={{ color: MP.textSecondary, fontSize: 18, flexShrink: 0 }} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      ) : (
        /* ── Desktop: card grid ── */
        <Grid container spacing={2}>
          {documents.map(doc => (
            <Grid item xs={12} sm={6} md={4} key={doc._id}>
              <DocumentCard
                doc={doc}
                selectable={selectionMode}
                selected={selectedIds.has(doc._id)}
                onSelect={selectionMode ? (checked) => toggleSelect(doc._id, checked) : onDocumentSelect}
                onEdit={() => onDocumentSelect(doc)}
                onDelete={() => handleDelete(doc)}
              />
            </Grid>
          ))}
        </Grid>
      )}
        </Box>
      </Box>

      <Dialog
        open={uploadOpen}
        onClose={() => { setUploadOpen(false); setAiSuggestions(null); setAiAnalyzing(false); setDuplicates([]); }}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flex: 1 }}>Upload to {selectedVault}</Box>
          {isMobile && (
            <IconButton size="small" onClick={() => { setUploadOpen(false); setAiSuggestions(null); setAiAnalyzing(false); setDuplicates([]); }}>
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {duplicates.length > 0 && (
            <DuplicateWarning
              duplicates={duplicates}
              onDismiss={() => setDuplicates([])}
              onViewDoc={(dup) => { setUploadOpen(false); setDuplicates([]); onDocumentSelect(dup); }}
            />
          )}
          <UploadZone onFileDrop={handleFileDrop} />

          {/* AI Analysis Status */}
          {aiAnalyzing && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, borderRadius: 1, bgcolor: 'primary.dark', mt: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption">🤖 AI analyzing document...</Typography>
            </Box>
          )}

          {/* AI Suggestions Banner */}
          {aiSuggestions && !aiAnalyzing && (
            <Paper sx={{ p: 1.5, mt: 1, bgcolor: 'rgba(76, 175, 80, 0.08)', border: '1px solid rgba(76,175,80,0.3)', borderRadius: 1 }}>
              <Typography variant="caption" color="success.main" fontWeight={700}>✨ AI Suggestions Applied</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                {aiSuggestions.vault && <Chip label={`Vault: ${aiSuggestions.vault}`} size="small" color="success" variant="outlined" />}
                {aiSuggestions.classification && <Chip label={`Class: ${aiSuggestions.classification}`} size="small" color="success" variant="outlined" />}
                {aiSuggestions.confidence && <Chip label={`${aiSuggestions.confidence}% confident`} size="small" color="success" variant="outlined" />}
              </Box>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7 }}>You can override any suggestion above</Typography>
            </Paper>
          )}

          {uploadFile && (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField label="Title" value={uploadForm.title} onChange={e => setUploadForm(p => ({ ...p, title: e.target.value }))} fullWidth />
              <TextField label="Description" value={uploadForm.description} onChange={e => setUploadForm(p => ({ ...p, description: e.target.value }))} fullWidth multiline rows={2} />
              <TextField label="Tags (comma-separated)" value={uploadForm.tags} onChange={e => setUploadForm(p => ({ ...p, tags: e.target.value }))} fullWidth />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                New uploads are always saved as <strong>Draft</strong>. Use Submit for Approval on the document page to publish.
              </Typography>
              <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                <InputLabel>Classification</InputLabel>
                <Select
                  value={uploadForm.classification || 'unclassified'}
                  onChange={e => setUploadForm(prev => ({ ...prev, classification: e.target.value }))}
                  label="Classification"
                >
                  {['unclassified', 'internal', 'confidential', 'restricted', 'secret'].map(c => (
                    <MenuItem key={c} value={c} sx={{ textTransform: 'capitalize' }}>
                      {c === 'secret' ? '🔐 SECRET' : c === 'restricted' ? '🔒 Restricted' : c === 'confidential' ? '🛡️ Confidential' : c === 'internal' ? '🏢 Internal' : '🌐 Unclassified'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setUploadOpen(false); setAiSuggestions(null); setAiAnalyzing(false); setDuplicates([]); }}>Cancel</Button>
          <Button variant="contained" onClick={handleUpload} disabled={!uploadFile || uploading}>
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      <BulkActionBar
        selectedIds={[...selectedIds]}
        onClearSelection={exitSelectionMode}
        onBulkArchive={handleBulkArchive}
        onBulkDelete={handleBulkDelete}
        onBulkClassify={handleBulkClassify}
      />
    </Box>
  );
}
